/**
 * Bulk add / set folder on selected presets
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { useState } from 'react';

import { Button, SearchableSelect } from '@/components/ui';

export interface PresetFolderBulkBarProps {
  folders: Array<{ id: string; label: string }>;
  /** Presets currently shown in the list (respects folder filter). */
  visiblePresetIds: string[];
  selectedPresetIds: string[];
  disabled: boolean;
  isBusy: boolean;
  onAddToFolder: (folderId: string) => void;
  onSetFolderOnly: (folderId: string) => void;
  onSelectAllVisible: () => void;
  onDeselectAll: () => void;
}

export const PresetFolderBulkBar: FC<PresetFolderBulkBarProps> = ({
  folders,
  visiblePresetIds,
  selectedPresetIds,
  disabled,
  isBusy,
  onAddToFolder,
  onSetFolderOnly,
  onSelectAllVisible,
  onDeselectAll,
}) => {
  const [folderId, setFolderId] = useState<string>('');
  const n = selectedPresetIds.length;
  const visibleCount = visiblePresetIds.length;
  const folderOpts = folders.map((f) => ({ value: f.id, label: f.label }));
  const picked = folderId.length > 0;

  const selectedSet = new Set(selectedPresetIds);
  const allVisibleSelected =
    visibleCount > 0 && visiblePresetIds.every((id) => selectedSet.has(id));

  if (visibleCount < 1) return null;

  return (
    <div className="flex flex-wrap items-end gap-2 border-b border-border/40 bg-muted/5 px-4 py-2">
      <div className="flex shrink-0 flex-col gap-1">
        <span className="text-[10px] font-medium uppercase text-muted-foreground">Selection</span>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-9"
          disabled={disabled}
          aria-label={allVisibleSelected ? 'Deselect all presets' : 'Select all visible presets'}
          onClick={() => (allVisibleSelected ? onDeselectAll() : onSelectAllVisible())}
        >
          {allVisibleSelected ? 'Deselect all' : 'Select all'}
        </Button>
      </div>
      <div className="flex min-w-[160px] flex-1 flex-col gap-1">
        <span className="text-[10px] font-medium uppercase text-muted-foreground">
          Apply folder to {n} selected{n > 0 ? '' : ' (use checkboxes or Select all)'}
        </span>
        <SearchableSelect
          className="h-9 rounded-md"
          options={folderOpts}
          value={folderId}
          onChange={(v) => setFolderId(String(v))}
          placeholder="Choose folder…"
          disabled={disabled || folders.length === 0}
        />
      </div>
      <Button
        type="button"
        size="sm"
        variant="secondary"
        className="h-9"
        disabled={disabled || isBusy || !picked || n < 1}
        onClick={() => picked && onAddToFolder(folderId)}
      >
        Add to folder
      </Button>
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="h-9"
        disabled={disabled || isBusy || !picked || n < 1}
        onClick={() => picked && onSetFolderOnly(folderId)}
      >
        Set folder only
      </Button>
    </div>
  );
};
